<template>
  <!-- existing assets -->
  <div v-for="asset of modelValue" :key="asset.pk" class="c-asset__existing-asset-list-element u-mbxs">
    <div class="u-pos-rel">
      <div class="c-asset__file">
        <code class="c-asset__file--metadata">
          {{ asset.name }}
          <span v-if="asset.size > 0" class="u-color-disabled">({{ formatBytes(asset.size) }})</span>
        </code>
      </div>
      <!-- remove -->
      <div v-if="editable && (asset.size > 0)" class="c-asset__file-actions">
        <button
          type="button"
          class="c-button c-button--medium c-button--transparent
                 c-asset__file-actions--button u-color-error"
          :title="t('remove')"
          @click.prevent="removeAsset(asset)"
        >
          <SVGIcon class="c-button__icon" name="cancel-square" />
        </button>
      </div>
    </div>
  </div>
  <!-- fields to upload new assets -->
  <template v-if="editable">
    <label class="c-field__choice c-field__asset-file-label">
      <input
        type="radio"
        name="asset_type"
        value="file"
        class="c-field__input"
        checked
        @click="switchAssetType(true)"
      >
      <span class="c-field__text">
        {{ t('file') }}
      </span>
      <div v-for="field of uploading" :key="field.id" class="u-mbxs">
        <div class="u-pos-rel">
          <ProjectAssetUpload
            :disabled="!isFileAsset"
            @update-status="field.status = $event"
            @success-upload="successUpload(field, $event)"
            @remove="uploading = uploading.filter(u => u.id !== field.id)"
          />
        </div>
      </div>
    </label>
    <label class="c-field__choice c-field__asset-url-label">
      <input
        type="radio"
        name="asset_type"
        value="link"
        class="c-field__input"
        @click="switchAssetType(false)"
      >
      <span class="c-field__text">
        {{ t('link') }}
      </span>
      <input
        id="asset-upload-link"
        type="text"
        name="asset_type"
        class="c-field__input c-field__input--visible"
        placeholder="URL"
        :disabled="isFileAsset"
      >
    </label>
  </template>
</template>

<script setup lang="ts">
import { ref, watch, watchEffect, computed } from 'vue'
import { useI18n } from '@/utils/i18n'
import { Asset } from '@/utils/api'
import SVGIcon from '@/components/SVGIcon.vue'
import ProjectAssetUpload from '@/components/ProjectAssetUpload.vue'
import { formatBytes } from '@/utils/format-bytes'

export type AssetFieldStatus = 'initial' | 'uploading' | 'error'

const props = defineProps<{
  modelValue: Asset[]
  editable: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', val: Asset[]): void
  (e: 'update:isUploading', val: boolean): void
  (e: 'update:isError', val: boolean): void
}>()

interface AssetForm {
  id: string
  status: AssetFieldStatus
}

const randomID = (): string => Math.random().toString(36).substr(2, 9)
const createAssetForm = (): AssetForm => ({ id: randomID(), status: 'initial' })

const isFileAsset = ref(true)

// list of assets being currently uploaded
const uploading = ref<AssetForm[]>([])

// always keep at least 1 AssetField to allow concurrent uploads
// and remove duplicated empty ('initial') fields
watch(uploading, () => {
  let hasOne = false
  const res = uploading.value.filter(u => {
    if (u.status !== 'initial') { return true }
    if (hasOne) { return false }
    hasOne = true
    return true
  })
  // add one initial if missing
  if (!hasOne) {
    res.push(createAssetForm())
  }
  // update only if array changed (avoid watch recursive loop)
  if (res.length !== uploading.value.length) {
    uploading.value = res
  }
}, { immediate: true, deep: true })

// invalid if files are being uploaded or have errors
const isUploading = computed(() => uploading.value.every(f => f.status !== 'uploading'))
const isError = computed(() => uploading.value.every(f => f.status !== 'error'))
watchEffect(() => { emit('update:isUploading', isUploading.value) })
watchEffect(() => { emit('update:isError', isError.value) })

function removeAsset (asset: Asset) {
  emit('update:modelValue', props.modelValue.filter(a => a.pk !== asset.pk))
}

function successUpload (field: AssetForm, asset: Asset) {
  // remove from currently uploading assets
  uploading.value = uploading.value.filter(u => u.id !== field.id)
  // add it to uploaded values
  emit('update:modelValue', props.modelValue.concat([ asset ]))
}

function switchAssetType (isDisabled: boolean) {
  isFileAsset.value = isDisabled
}

const { t } = useI18n({
  messages: {
    en: {
      remove: 'Remove the asset',
      file: 'Add a file',
      link: 'Add a link'
    },
    fr: {
      remove: 'Supprimer le fichier',
      file: 'Ajouter un fichier',
      link: 'Ajouter un lien'
    }
  }
})
</script>
